import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ReactComponent as ClockIcon } from '../../assets/images/clock-icon.svg';
import { ReactComponent as ClockGreenIcon } from '../../assets/images/clock-green.svg';
import { ReactComponent as ClockRedIcon } from '../../assets/images/clock-red.svg';

import { ReactComponent as EmptyClockIcon } from '../../assets/images/empty-clock.svg';
import { ReactComponent as LocationIcon } from '../../assets/images/location-icon.svg';
import Timer from '../timer';
import DocketButton from './print-docket';
import ReactToPrint from 'react-to-print';
import OrderSticker from '../card-header/order-sticker';
import {
  statusIsCompleted,
  statusIsDispatch,
  statusIsPackaging,
  statusIsPending,
  statusIsReadyDispatchOrPending,
  statusIsReadyOrDispatch,
  statusIsRejected
} from '../../helpers/permissions.service';
import useLayoutContext from '../../contexts/layout.context';
import { isOrderReady } from '../../helpers/functions';

const CardFooter = ({
  status,
  order,
  time,
  reason,
  riderAssigned,
  receiptRef,
  printStickerMeals
}) => {
  const { permissions } = useLayoutContext();
  const {
    showIfOrderIsReady,
    showDocketButton,
    isKitchenRole,
    showOrderLocation,
    canViewRiderNote
  } = permissions;
  const [displayTime, setDisplayTime] = useState(null);
  const [isLate, setIsLate] = useState(false);
  const showTimer = statusIsPackaging(status);
  const stickerRef = useRef(null);
  const showRiderInfo = statusIsReadyOrDispatch(status);
  const PREP_MS = 35 * 60 * 1000;

  const isReady = () => {
    return showIfOrderIsReady && isOrderReady(order);
  };

  const updateTimer = useCallback(() => {
    if (!showTimer) {
      setDisplayTime(null);
      return;
    }
    const startedTs = new Date(time).getTime();
    const now = Date.now();
    const elapsed = now - startedTs;
    const remaining = PREP_MS - elapsed;

    if (remaining >= 0) {
      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      if (hours > 0) {
        setDisplayTime(
          `${hours}h ${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        );
      } else {
        setDisplayTime(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
      }
    } else {
      setIsLate(true);
      const minutesLate = Math.ceil(-remaining / 60000);
      const hoursLate = Math.floor(minutesLate / 60);
      const remainingMinutes = minutesLate % 60;

      if (hoursLate > 0) {
        setDisplayTime(`${hoursLate}h ${remainingMinutes}m late`);
      } else {
        setDisplayTime(`${minutesLate} mins late`);
      }
    }
  }, [showTimer, time]);

  useEffect(() => {
    let intervalId = null;
    if (statusIsPackaging(status)) {
      updateTimer();
      intervalId = setInterval(updateTimer, 1000);
    } else {
      setDisplayTime(null);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showTimer, updateTimer]);

  return (
    !statusIsDispatch(status) && (
      <div className='mt-6'>
        {!(isKitchenRole && statusIsPending(status)) && (
          <div
            className={`flex ${
              statusIsReadyDispatchOrPending(status) ? 'items-end' : 'items-start'
            } gap-6 justify-between text-white`}>
            <div className='flex flex-col gap-2 relative'>
              {showOrderLocation && !order?.calculated_order?.pickup && (
                <div className='flex justify-start gap-3 items-center '>
                  <LocationIcon />
                  <p className='font-medium '>
                    {order?.calculated_order?.address_details?.address_line}
                  </p>
                </div>
              )}
              {!statusIsRejected(status) ? (
                <div className='flex justify-start gap-3 items-center'>
                  <EmptyClockIcon />
                  <p className='text-[12px] opacity-70  '>
                    {statusIsCompleted(status) ? 'Completed: ' : 'Time since order: '}
                    {time && <Timer time={time} format={statusIsCompleted(status) ? 'LLLL' : ''} />}
                  </p>
                </div>
              ) : null}

              <div className='flex flex-col gap-3 relative'>
                {order?.kitchen_verified_time && (
                  <>
                    <div className='flex justify-start gap-3 items-center'>
                      <ClockIcon />
                      <p className='text-[12px] opacity-70'>
                        Received by kitchen:&nbsp;
                        {order?.kitchen_verified_time && (
                          <Timer time={order.kitchen_verified_time} />
                        )}
                      </p>
                    </div>
                    <div className='absolute  left-[8px] h-[24px] w-[1px] bg-white opacity-[0.12] bottom-[8px]'></div>
                  </>
                )}
                {order?.kitchen_completed_time ? (
                  <div className='flex justify-start gap-3 items-center'>
                    <ClockIcon />
                    <p className='text-[12px] opacity-70'>
                      Received by frontdesk:&nbsp;
                      <Timer time={order.kitchen_completed_time} />
                    </p>
                  </div>
                ) : null}
              </div>

              {statusIsRejected(status) ? (
                <div className=' flex flex-col gap-3'>
                  <p className='text-[12px] opacity-70 mb-1 '>
                    REJECTED : {time && <Timer time={time} format='LLLL' />}
                  </p>
                  <p className='text-[12px] opacity-70 mb-0 bold mr-auto'>Reason: {reason}</p>
                </div>
              ) : null}
            </div>

            <div className=' flex flex-col gap-3 '>
              <div className='flex flex-col gap-3 items-start'>
                {riderAssigned === 'no' && !order?.calculated_order?.pickup ? (
                  <p className='text-[12px] opacity-70 mb-1'> RIDER NOT YET ASSIGNED</p>
                ) : null}
                {showTimer && (
                  <div
                    className='flex gap-2 bg-white rounded-lg p-2 flex-nowrap'
                    style={{ minWidth: '150px' }}>
                    {isLate ? (
                      <ClockRedIcon className='w-5 h-5' />
                    ) : (
                      <ClockGreenIcon className='w-5 h-5' />
                    )}
                    <p className='text-sm text-black'>
                      Prep Time:
                      <strong style={{ color: isLate ? '#ef4444' : '#22c55e' }}>
                        {displayTime}
                      </strong>{' '}
                    </p>{' '}
                  </div>
                )}
                {showRiderInfo && (
                  <p>
                    Rider:
                    <span className='font-medium'>
                      {order?.rider?.first_name
                        ? `${order?.rider?.first_name} ${order?.rider?.last_name}`
                        : 'Rider not assigned'}
                    </span>
                  </p>
                )}
                {canViewRiderNote &&
                  order?.order_details?.rider_note &&
                  (order?.calculated_order?.pickup ? (
                    <p className=''>
                      Pickup Note:
                      <span> {order?.order_details?.rider_note}</span>
                    </p>
                  ) : (
                    <p className=''>
                      Rider Note:
                      <span> {order?.order_details?.rider_note}</span>
                    </p>
                  ))}
              </div>
            </div>
            <div className='flex flex-col items-end gap-3'>
              {isReady() && printStickerMeals?.length > 0 && (
                <>
                  <ReactToPrint
                    trigger={() => (
                      <p className='text-xs cursor-pointer underline-offset-1 text-white font-thin underline'>
                        Print Sticker
                      </p>
                    )}
                    content={() => stickerRef.current}
                  />
                  <div className='no-print print-sticker' ref={stickerRef}>
                    <OrderSticker order={order} orders={printStickerMeals} />
                  </div>
                </>
              )}
              {showDocketButton && <DocketButton receiptRef={receiptRef} status={status} />}
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default CardFooter;
